import React, { memo } from 'react';
import styled, { keyframes } from 'styled-components';

import colors from '@front/common/src/utils/colors';

const keyFrame = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

export const LoadingSpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Spinner = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
`;

const SpinnerBar = styled.div<{ $height: number; $width: number; $radius: number; $color: string }>`
  transform-origin: ${({ $height }) => $height}px ${({ $height }) => $height}px;
  animation: ${keyFrame} 0.8s linear infinite;

  &::after {
    content: ' ';
    display: block;
    position: absolute;
    top: ${({ $width }) => $width / 2}px;
    left: ${({ $width, $height }) => $height - $width / 2}px;
    width: ${({ $width }) => $width}px;
    height: ${({ $height }) => $height / 1.7}px;
    border-radius: ${({ $radius }) => $radius}px;
    background: ${({ $color }) => $color};
  }

  &:nth-child(1) {
    transform: rotate(0deg);
    animation-delay: -0.7s;
  }

  &:nth-child(2) {
    transform: rotate(45deg);
    animation-delay: -0.6s;
  }

  &:nth-child(3) {
    transform: rotate(90deg);
    animation-delay: -0.5s;
  }

  &:nth-child(4) {
    transform: rotate(135deg);
    animation-delay: -0.4s;
  }

  &:nth-child(5) {
    transform: rotate(180deg);
    animation-delay: -0.3s;
  }

  &:nth-child(6) {
    transform: rotate(225deg);
    animation-delay: -0.2s;
  }

  &:nth-child(7) {
    transform: rotate(270deg);
    animation-delay: -0.1s;
  }

  &:nth-child(8) {
    transform: rotate(315deg);
    animation-delay: 0s;
  }
`;

type SpinnerSize = 'md' | 'lg';

const spinnerParams: Record<SpinnerSize, { height: number; width: number; radius: number }> = {
  md: {
    height: 10,
    width: 2,
    radius: 2,
  },
  lg: {
    height: 40,
    width: 8,
    radius: 4,
  },
};

export type LoadingSpinnerProps = {
  color?: string;
  className?: string;
  size?: SpinnerSize;
  title?: string;
};

const LoadingSpinner = ({ color = colors.primary100, size = 'md', title, className }: LoadingSpinnerProps) => {
  const { height, width, radius } = spinnerParams[size];
  return (
    <Spinner $size={height * 2} className={className} aria-label={title}>
      <SpinnerBar $height={height} $color={color} $width={width} $radius={radius} data-testid='spinner-elmt-1' />
      <SpinnerBar $height={height} $color={color} $width={width} $radius={radius} />
      <SpinnerBar $height={height} $color={color} $width={width} $radius={radius} />
      <SpinnerBar $height={height} $color={color} $width={width} $radius={radius} />
      <SpinnerBar $height={height} $color={color} $width={width} $radius={radius} />
      <SpinnerBar $height={height} $color={color} $width={width} $radius={radius} />
      <SpinnerBar $height={height} $color={color} $width={width} $radius={radius} />
      <SpinnerBar $height={height} $color={color} $width={width} $radius={radius} />
    </Spinner>
  );
};

export default memo(LoadingSpinner);
